<script setup>
  import {computed, inject, onBeforeMount, onMounted, ref, watch} from "vue";

  import ComponentRowOriginFromMergeBadge from "@components/Badges/RowOriginFromMergeBadge.vue";
  import ComponentFullDateInput from "@components/Inputs/FullDateInput.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {usePage} from "@inertiajs/vue3";
  import {laborbardeviationValues} from "@pages/Records/Components/Sections/LaborBar/enums.js";
  import {
    getDeviationFromValue,
    getReferenceText,
    getReferenceValue,
  } from "@pages/Records/Components/Sections/LaborBar/Utils/laborvalue.js";
  import {MedButton, MedTooltip} from "@pharma4u/medicheck-components";
  import NumberHelper from "@utils/Helpers/NumberHelper.js";

  defineOptions({
    name: "MainLaborValuesBmiRow",
  });

  const props = defineProps({
    isEditable: {
      type: Boolean,
      default: false,
    },
    deletable: {
      type: Boolean,
      default: true,
    },
    size: {
      type: Object,
      required: true,
    },
    weight: {
      type: Object,
      required: true,
    },
    bmi: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(["update"]);

  const page = usePage();

  const deleteDialog = inject("recordEditDeleteRowDialog");

  const weightError = ref("");
  const sizeValue = ref("");

  const form = ref({
    size: props.size ?? {},
    weight: props.weight ?? {},
    bmi: props.bmi ?? {},
  });

  const fieldsDisabled = computed(() => !props.isEditable);

  const sizeError = computed(() => (sizeValue.value[0] >= 3 ? "Keine sinnvolle Größe" : ""));

  const weightValue = computed(() => {
    return form.value.weight.value
      ? NumberHelper.formatFloatAsGermanNumberString(NumberHelper.formatStringAsFloat(form.value.weight.value))
      : "";
  });

  const bmiReferenceValue = computed(() => {
    return getReferenceValue(form.value.bmi, page.props.patient.gender);
  });

  const bmiReferenceText = computed(() => {
    if (!bmiReferenceValue.value || !form.value.bmi.value || form.value.bmi.value === "") {
      return null;
    }

    return getReferenceText(bmiReferenceValue.value);
  });

  watch(
    () => ({size: props.size, weight: props.weight, bmi: props.bmi}),
    (newProperties) => {
      form.value.size = newProperties.size ?? {};
      if (newProperties?.size?.value === null) {
        sizeValue.value = "";
      }
      form.value.weight = newProperties.weight ?? {};
      form.value.bmi = newProperties.bmi ?? {};
    },
    {deep: true},
  );

  watch(
    () => form.value.bmi.value,
    (newValue) => {
      if (
        newValue !== null &&
        form.value.size.value &&
        form.value.weight.value &&
        newValue !==
          Math.round(
            Number.parseFloat(form.value.weight.value.replace(",", ".")) /
              Math.pow(Number.parseFloat(form.value.size.value.replace(",", ".")), 2),
          ).toString()
      ) {
        form.value.weight.value = null;
      }

      if (bmiReferenceValue.value) {
        form.value.bmi.deviation = getDeviationFromValue(
          form.value.bmi.value,
          bmiReferenceValue.value.low,
          bmiReferenceValue.value.high,
        );
      }

      emitUpdate();
    },
  );

  onBeforeMount(() => {
    if (form.value.bmi?.core && form.value.bmi.core.unitList.length > 0 && !form.value.bmi.mc_unit_id) {
      form.value.bmi.mc_unit_id = form.value.bmi.core.unitList[0].id;
      form.value.bmi.unit = form.value.bmi.core.unitList[0].unit;
    }
  });

  onMounted(() => {
    sizeValue.value = form.value.size.value
      ? NumberHelper.formatFloatAsGermanNumberString(NumberHelper.formatStringAsFloat(form.value.size.value))
      : "";

    if (form.value.bmi.value === null && form.value.bmi.id === null && form.value.bmi.deviation === "normal") {
      // try to calculate bmi once to force correct bmi/deviation after importing a record
      // (bmi does not exist at this moment, so its .id is null)
      tryToCalculateBmi();

      if (form.value.bmi.value !== null) {
        // save, if bmi has been changed after calling tryToCalculateBmi();
        emitUpdate();
      }
    }
  });

  const tryToCalculateBmi = () => {
    if (form.value.size.value && form.value.weight.value) {
      const size = NumberHelper.formatStringAsFloat(form.value.size.value);
      const weight = NumberHelper.formatStringAsFloat(form.value.weight.value);

      form.value.bmi.value = Math.round(weight / Math.pow(size, 2)).toString();
      form.value.bmi.unit = form.value.bmi.core.unitList[0].unit;
      form.value.bmi.mc_unit_id = form.value.bmi.core.unitList[0].id;
      form.value.bmi.deviation = getDeviationFromValue(
        NumberHelper.formatStringAsFloat(form.value.bmi.value),
        bmiReferenceValue.value.low,
        bmiReferenceValue.value.high,
      );
    }
  };

  const updateSize = (newValue) => {
    let correctedValue = null;

    if (newValue) {
      correctedValue = newValue.replace("m", "").replace(".", ",").trim();

      // automatically add a comma if the number does not make sense otherwise
      if (correctedValue.length === 2 && correctedValue[1] !== ",") {
        correctedValue = correctedValue[0] + "," + correctedValue[1];
      }
    }

    sizeValue.value = correctedValue ?? "";

    if (sizeError.value.length > 0) {
      return;
    }

    form.value.size.value = correctedValue;
    form.value.size.unit = form.value.size.core.unitList[0].unit;
    form.value.size.mc_unit_id = form.value.size.core.unitList[0].id;

    tryToCalculateBmi();

    emitUpdate();
  };

  const updateWeight = (newValue) => {
    if (newValue >= 400) {
      weightError.value = "Kein sinnvolles Gewicht";
      return;
    } else {
      weightError.value = null;
    }

    if (sizeError.value.length > 0) {
      return;
    }

    if (newValue) {
      form.value.weight.value = newValue.toString().replace("kg", "").replace(",", ".").trim();
    } else {
      form.value.weight.value = null;
    }

    form.value.weight.unit = form.value.weight.core.unitList[0].unit;
    form.value.weight.mc_unit_id = form.value.weight.core.unitList[0].id;

    tryToCalculateBmi();

    emitUpdate();
  };

  const clearBmi = () => {
    form.value.bmi.value = null;

    form.value.bmi.unit = form.value.bmi.core.unitList[0].unit;
    form.value.bmi.mc_unit_id = form.value.bmi.core.unitList[0].id;

    form.value.bmi.deviation = getDeviationFromValue(
      form.value.bmi.value,
      bmiReferenceValue.value.low,
      bmiReferenceValue.value.high,
    );

    emitUpdate();
  };

  const updateBmiDeviation = (newBmiDevation) => {
    form.value.bmi.deviation = newBmiDevation;
    form.value.bmi.value = null;
    emitUpdate();
  };

  const updateBmiDate = (newBmiDate) => {
    form.value.bmi.date = newBmiDate;
    emitUpdate();
  };

  const emitUpdate = () => {
    emit("update");
  };

  const showDeleteConfirmDialog = () => {
    // set value 1 as second parameter ("overrideLength") to force the dialog
    // not to show the real amount of internal labor values being deleted (3)
    deleteDialog(
      {
        routeName: "laborvalues",
        payload: {
          laborvalue: [form.value.weight.id, form.value.size.id, form.value.bmi.id].filter(
            (id) => id !== undefined && id !== null,
          ),
        },
      },
      1, // << override length!
    );
  };
</script>

<template>
  <li class="grid grid-cols-13 gap-x-6 py-2">
    <!-- row 0 - optional origin when merging records -->
    <ComponentRowOriginFromMergeBadge
      :hashid="size?.id ?? weight?.id ?? bmi?.id"
      type="laborvalues"
      class="col-span-12 ml-8"
    />

    <div class="col-span-3">
      <div class="flex items-center">
        <template v-if="isEditable && $slots.prefix">
          <div class="mt-3">
            <slot name="prefix" />
          </div>
        </template>

        <ComponentInput
          class="ml-2"
          label="Größe in m"
          :model-value="sizeValue"
          :error="sizeError"
          :disabled="fieldsDisabled"
          :validation="sizeError"
          :test-id="getTestId('row-size')"
          @input="updateSize"
          @clear="updateSize"
        />
      </div>
    </div>

    <div class="col-span-3">
      <ComponentInput
        label="Gewicht in kg"
        :model-value="weightValue"
        :error="weightError"
        :disabled="fieldsDisabled"
        :validation="weightError"
        :test-id="getTestId('row-weight')"
        @input="updateWeight"
        @clear="updateWeight"
      />
    </div>

    <div class="col-span-2">
      <ComponentInput
        v-model="form.bmi.value"
        label="BMI in kg/m²"
        :helper-text="bmiReferenceText"
        :disabled="fieldsDisabled"
        :test-id="getTestId('row-value')"
        @clear="clearBmi"
      />
    </div>

    <div :class="{'col-span-2': deletable, 'col-span-3': !deletable}">
      <ComponentSelect
        :id="'deviation' + form.bmi.id"
        :name="'deviation' + form.bmi.id"
        :model-value="form.bmi.deviation"
        :options="laborbardeviationValues"
        key-name="name"
        value-name="value"
        label="Abweichung:"
        :disabled="fieldsDisabled"
        :test-id="getTestId('row-deviation')"
        @change="updateBmiDeviation"
      />
    </div>

    <div class="col-span-2">
      <ComponentFullDateInput
        ref="date"
        label="Von (Datum):"
        :model-value="form.bmi.date"
        :disabled="fieldsDisabled"
        :test-id="getTestId('row-date')"
        @input="updateBmiDate"
      />
    </div>

    <template v-if="deletable">
      <div class="col-span-1 items-baseline px-2 pt-6">
        <MedTooltip content="Zeile löschen">
          <MedButton
            aria-label="BMI-Eintrag löschen"
            flat
            icon="delete"
            rounded="full"
            :data-test="getTestId('row-delete-button')"
            @click="showDeleteConfirmDialog"
          />
        </MedTooltip>
      </div>
    </template>
  </li>
</template>
